import { client, CONTENT_BASE_URL } from "@/api/client.ts";
import { APP_ID } from "@/env.ts";
import { USER_BASE_URL } from "./internal-const";

export type ItemType = "collection" | "video" | "tab" | "sectionHeader";

export interface CollectionItem {
  ChildId: string;
  DataSource: string;
  Type: ItemType;
  TemplateId: string | null;
  Title: string;
  Thumbnail: string;
  DurationSeconds: string; //Videos
  ProductId: string; // Videos
  ItemCount: number; //Collections
}

export interface CollectionResponse extends Collection {
  ChildCollections: Record<number, Collection>;
  ChildVideos: Record<number, Video>;
}

export interface Resource {
  OriginalUrl?: string;
  ResourceId: number;
  Title: string;
  Type: string;
  Url: string;
}

export interface Collection extends ContentThumbnails {
  TabId: number;
  Name: string;
  TemplateId: string;
  ThumbnailSource: string;
  Description: string;
  Type: string;
  DataSource: string;
  SourceId: string;
  ModularViewDisplayLimit: number;
  Published: string;
  Tag: string;
  Items: CollectionItem[];
  Properties: Record<string, string>; // TODO Types
  Resources: Resource[];
  SourceProductIDs?: string[];
  HideFromSearchResults?: 1 | 0;
  Drip?: 1 | 0;
  DripDays?: number;

  // Main Tab
  Position: number;
  IsMainTab: 1 | 0;
  Icon: string;
  NavBarTitleText: string;
}

export interface VideoFile {
  URL: string;
  Type: string;
  Size: number;
  SizeFormatted?: string;
  LanguageCode?: string;
  LanguageDisplayName?: string;
  Status?: string; // Livestream status
}

export interface Offer {
  DataSource: string;
  Id: number;
  ProductId: string;
  ProductIdWeb: string;
  SourceProductId: string;
  ReadyForSaleWeb: string; //yes/no/null
  DisplayName: string;
  FinalDisplayName: string;
  SubscriptionDuration: string;
  SubscriptionTrial: string;
  TrialDisplay: string;
  WebPriceUSD: number;
  Description: string;
}

export const OFFER_DURATION_1M = "1m";
export const OFFER_DURATION_3M = "3m";
export const OFFER_DURATION_6M = "6m";
export const OFFER_DURATION_1Y = "1y";

export const SUPPORTED_OFFER_DURATIONS = [
  OFFER_DURATION_1M,
  OFFER_DURATION_3M,
  OFFER_DURATION_6M,
  OFFER_DURATION_1Y,
];

export type SupportedOfferDuration = (typeof SUPPORTED_OFFER_DURATIONS)[number];

export type ThumbnailSize = "small" | "medium" | "large" | "source" | "";
export type ThumbnailType =
  | "square"
  | "portrait"
  | "landscape"
  | "wideLandscape";

export interface ContentThumbnails {
  SourceThumbnailSource: string;
  ThumbnailSource: string;
  Thumbnails: Partial<Record<ThumbnailSize, string>>;
  SquareThumbnails?: Partial<Record<ThumbnailSize, string>>;
  PortraitThumbnails?: Partial<Record<ThumbnailSize, string>>;
  SourceSquareThumbnailSource?: string;
  SourcePortraitThumbnailSource?: string;
  SquareThumbnailSource?: string;
  PortraitThumbnailSource?: string;
}

export interface Video extends ContentThumbnails {
  AppId: string;
  DataSource: string;
  DurationSeconds: string;
  OriginalFilename: string;
  PreviewVideoId?: string;
  InstructionalVideoId?: string;
  SourceId: string;
  SourceTitle: string;
  Tag?: string;
  Resources?: Resource[];
  Title: string;
  Type: string;
  VideoId: number;
  ProductId?: string;
  Properties?: Record<string, string>;
  Files?: VideoFile[];
  CreatedAt?: string;
  Description: string;
  SourceProductIDs?: string[];
  HideFromSearchResults?: 1 | 0;
}

export type VideoResult = Pick<
  Video,
  | "VideoId"
  | "Thumbnails"
  | "DurationSeconds"
  | "DataSource"
  | "Title"
  | "SourceProductIDs"
>;
export type ColResult = Pick<
  Collection,
  "TabId" | "Name" | "Thumbnails" | "DataSource" | "SourceProductIDs"
> & { ItemCount: number };

export interface SearchResultsResponse {
  videos: VideoResult[];
  collections: ColResult[];
}

export type AppProperties = Record<string, string>; //TODO List actual properties

export interface MainTabsResponse {
  Tabs: Collection[];
  Collections: Collection[];
  Videos: Video[];
}

// Format to pass own a full set of Videos/Collections for a collection
// Not just the CollectionItems
export interface FullItems {
  videos: Record<number, Video>;
  collections: Record<number, Collection>;
}

export interface CalendarItem {
  Published: number;
  Quote: string;
  Items: { Type: ItemType; Id: number }[];
}

export type CalendarResponse = Record<
  string,
  Record<string, CalendarItem | string>
>;

interface CalendarData {
  dates: Record<string, Record<string, CalendarItem>>;
  journalPrompts: Record<number, string>;
}

/*
 *  API calls to retrieve content
 */

export const getMainTabs = () => {
  return client
    .get<MainTabsResponse>(
      `${CONTENT_BASE_URL}/api/v2/data/${APP_ID}/live/maintab?version=2024-07`,
      {},
    )
    .then((data) => data.data);
};

export const getCollections = (ids: number[]) => {
  return client
    .get<{
      Collections: Collection[];
    }>(`${CONTENT_BASE_URL}/api/v2/items/${APP_ID}/live/collection`, {
      params: { ids: ids.join(",") },
    })
    .then((data) => data.data?.Collections);
};

export const getVideos = (ids: number[]) => {
  return client
    .get<{ Videos: Video[] }>(
      `${CONTENT_BASE_URL}/api/v2/items/${APP_ID}/live/video`,
      {
        params: { ids: ids.join(",") },
      },
    )
    .then((data) => data.data?.Videos);
};

export const getItemContent = (
  itemId: number,
  itemType: "video" | "collection",
) => {
  return client
    .get(
      `${CONTENT_BASE_URL}/api/v2/item/${APP_ID}/live/${itemType}/${itemId}`,
      {},
    )
    .then((data) => data.data);
};

export const getAppProperties = () => {
  return client
    .get(`${CONTENT_BASE_URL}/api/v2/data/${APP_ID}/live/properties`, {})
    .then((data) => data.data);
};

export const getOffers = () => {
  return client
    .get<Offer[]>(`${CONTENT_BASE_URL}/api/v2/data/${APP_ID}/live/products`, {})
    .then((data) => data.data);
};

export const searchContent = (payload: {
  query: string;
  tags_by_category?: string[][];
  min_duration?: number;
  max_duration?: number;
}) => {
  return client
    .post<SearchResultsResponse>(
      `${CONTENT_BASE_URL}/api/v2/search/${APP_ID}/live`,
      payload,
    )
    .then((data) => data.data);
};

export const getCalendar = () => {
  return client
    .get<CalendarResponse>(
      `${CONTENT_BASE_URL}/api/v2/data/${APP_ID}/builder/calendar`,
      {},
    )
    .then(({ data }) => {
      const calendarData: CalendarData = { dates: {}, journalPrompts: [] };
      Object.entries(data).forEach(([key, value]) => {
        if (key === "journal") {
          calendarData.journalPrompts = value as Record<number, string>;
        } else {
          calendarData.dates[key] = value as Record<string, CalendarItem>;
        }
      });
      return calendarData;
    });
};

export const getVideoFiles = async (
  videoId: string | number,
): Promise<VideoFile[]> => {
  const { data } = await client.get(`${USER_BASE_URL}/api/video-files/${videoId}`, {
    params: {
      app_id: APP_ID,
    },
  });
  if ("error" in data) {
    console.error(`Failed to get VideoFiles for videoId:${videoId}`, data);
    return [];
  }
  return data;
};
